import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { dispatch } from '../store';
import { updateError } from './user';

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState = {
    calendly: {
        config: null,       // null = not connected, object = connected
        loading: false,
        error: null,
    },
    calcom: {
        practiceId: null,
        config: null,
        loading: false,
        error: null,
    },
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const slice = createSlice({
    name: 'bookingIntegration',
    initialState,
    reducers: {
        setCalendlyLoading(state, action) {
            state.calendly.loading = action.payload;
            state.calendly.error = null;
        },
        setCalendlyConfig(state, action) {
            state.calendly.config = action.payload;
            state.calendly.loading = false;
            state.calendly.error = null;
        },
        setCalendlyError(state, action) {
            state.calendly.error = action.payload;
            state.calendly.loading = false;
        },
        setCalcomLoading(state, action) {
            const { practiceId, loading } = action.payload;
            if (String(state.calcom.practiceId) !== String(practiceId)) state.calcom.config = null;
            state.calcom.practiceId = practiceId;
            state.calcom.loading = loading;
            state.calcom.error = null;
        },
        setCalcomConfig(state, action) {
            if (String(state.calcom.practiceId) !== String(action.payload.practiceId)) return;
            state.calcom.config = action.payload.config;
            state.calcom.loading = false;
            state.calcom.error = null;
        },
        setCalcomError(state, action) {
            if (String(state.calcom.practiceId) !== String(action.payload.practiceId)) return;
            state.calcom.error = action.payload.error;
            state.calcom.loading = false;
        },
    },
});

export default slice.reducer;

// ─── Calendly thunks ──────────────────────────────────────────────────────────

export function getCalendlyConfig(practiceId) {
    return async () => {
        if (!hasFeatureAccess('calendly-integration')) return;
        dispatch(slice.actions.setCalendlyLoading(true));
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/calendly-config/`);
            if (response.status === 200) {
                dispatch(slice.actions.setCalendlyConfig(response.data));
            } else if (response.status === 404) {
                dispatch(slice.actions.setCalendlyConfig(null));
            } else {
                dispatch(slice.actions.setCalendlyError(response.data?.detail || 'Failed to load Calendly config'));
            }
        } catch (err) {
            dispatch(slice.actions.setCalendlyError(err.message || 'Something went wrong'));
        }
    };
}

export function connectCalendly(practiceId, token, onSuccess, onError) {
    return async () => {
        if (!hasFeatureAccess('calendly-integration')) return;
        dispatch(slice.actions.setCalendlyLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calendly-config/`,
                'POST',
                { personal_access_token: token }
            );
            if (response.status === 201 || response.status === 200) {
                dispatch(slice.actions.setCalendlyConfig(response.data));
                if (onSuccess) onSuccess();
            } else {
                const msg = response.data?.error || 'Failed to connect Calendly';
                dispatch(slice.actions.setCalendlyError(msg));
                if (onError) onError(msg);
            }
        } catch (err) {
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setCalendlyError(msg));
            if (onError) onError(msg);
        }
    };
}

export function updateCalendlyEventTypes(practiceId, eventTypes, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('calendly-integration')) return;
        dispatch(slice.actions.setCalendlyLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calendly-config/event-types/`,
                'PUT',
                eventTypes
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCalendlyConfig(response.data));
                if (onSuccess) onSuccess();
            } else {
                dispatch(slice.actions.setCalendlyError(response.data?.detail || 'Failed to update event types'));
            }
        } catch (err) {
            dispatch(slice.actions.setCalendlyError(err.message || 'Something went wrong'));
        }
    };
}

export function disconnectCalendly(practiceId, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('calendly-integration')) return;
        dispatch(slice.actions.setCalendlyLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calendly-config/`,
                'DELETE'
            );
            if (response.status === 204) {
                dispatch(slice.actions.setCalendlyConfig(null));
                dispatch(updateError({ success: true, message: 'Calendly disconnected' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(slice.actions.setCalendlyError('Failed to disconnect Calendly'));
            }
        } catch (err) {
            dispatch(slice.actions.setCalendlyError(err.message || 'Something went wrong'));
        }
    };
}

let calcomRequestGeneration = 0;

// ─── Cal.com thunks ───────────────────────────────────────────────────────────

export function getCalcomConfig(practiceId) {
    return async () => {
        calcomRequestGeneration += 1;
        const requestGeneration = calcomRequestGeneration;
        if (!hasFeatureAccess('calcom-integration')) return;
        dispatch(slice.actions.setCalcomLoading({ practiceId, loading: true }));
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/calcom-config/`);
            if (requestGeneration !== calcomRequestGeneration) return;
            if (response.status === 200) {
                dispatch(slice.actions.setCalcomConfig({ practiceId, config: response.data }));
            } else if (response.status === 404) {
                dispatch(slice.actions.setCalcomConfig({ practiceId, config: null }));
            } else {
                dispatch(slice.actions.setCalcomError({ practiceId, error: response.data?.detail || 'Failed to load Cal.com config' }));
            }
        } catch (err) {
            if (requestGeneration !== calcomRequestGeneration) return;
            dispatch(slice.actions.setCalcomError({ practiceId, error: err.message || 'Something went wrong' }));
        }
    };
}

export function saveCalcomConfig(practiceId, data, onSuccess, onError) {
    return async () => {
        calcomRequestGeneration += 1;
        const requestGeneration = calcomRequestGeneration;
        if (!hasFeatureAccess('calcom-integration')) return;
        dispatch(slice.actions.setCalcomLoading({ practiceId, loading: true }));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calcom-config/`,
                'POST',
                data
            );
            if (requestGeneration !== calcomRequestGeneration) return;
            if (response.status === 201 || response.status === 200) {
                dispatch(slice.actions.setCalcomConfig({ practiceId, config: response.data }));
                if (onSuccess) onSuccess();
            } else {
                const msg = response.data?.detail || response.data?.error || response.data?.api_key?.[0] || response.data?.non_field_errors?.[0] || 'Failed to save Cal.com config';
                dispatch(slice.actions.setCalcomError({ practiceId, error: msg }));
                if (onError) onError(msg);
            }
        } catch (err) {
            if (requestGeneration !== calcomRequestGeneration) return;
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setCalcomError({ practiceId, error: msg }));
            if (onError) onError(msg);
        }
    };
}

export function disconnectCalcom(practiceId, onSuccess) {
    return async () => {
        calcomRequestGeneration += 1;
        const requestGeneration = calcomRequestGeneration;
        if (!hasFeatureAccess('calcom-integration')) return;
        dispatch(slice.actions.setCalcomLoading({ practiceId, loading: true }));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calcom-config/`,
                'DELETE'
            );
            if (requestGeneration !== calcomRequestGeneration) return;
            if (response.status === 204) {
                dispatch(slice.actions.setCalcomConfig({ practiceId, config: null }));
                dispatch(updateError({ success: true, message: 'Cal.com disconnected' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(slice.actions.setCalcomError({ practiceId, error: 'Failed to disconnect Cal.com' }));
            }
        } catch (err) {
            if (requestGeneration !== calcomRequestGeneration) return;
            dispatch(slice.actions.setCalcomError({ practiceId, error: err.message || 'Something went wrong' }));
        }
    };
}
